import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { getCasinoGameDetails, getMatchExposure } from "@/services/casinoApi";
import BetTableTeen8 from "../components/teen8/BetTableTeen8";
import ResultTeen8 from "../components/teen8/ResultTeen8";
import RightSection from "../components/RightSection/RightSection";
import styles from "./Teen8.module.css";
import VideoBox from "../components/common/VideoBox";
import CasinoHeading from "../components/common/CasinoHeading";

function extractCasinoGame(url) {
  const match = url.match(/\/lord-games\/([^/?#]+)/);
  return match ? match[1] : null;
}

// Helper function to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str.replace(/\s+/g, ' ').trim().toLowerCase();
};

// Helper function to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [
    selection,
    selection.toLowerCase(),
    selection.toLowerCase().trim(),
    selection.trim(),
    normalizeString(selection),
  ];
  return [...new Set(keys.filter(k => k))];
};

export default function Teen8() {
  const [gameData, setGameData] = useState({});
  const [activeTab, setActiveTab] = useState("game");
  const [iframeSrc, setIframeSrc] = useState("");
  const { pathname } = useLocation();
  const [filteredData, setFilteredData] = useState([]);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);

  useEffect(() => {
    const name = extractCasinoGame(pathname) || "teen8";
    setIframeSrc(`https://casino-stream.softgamingapi.com/casino-tv?id=${name}`);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching game data:", err);
      }
    };

    // Start API calls after 5 seconds delay
    let intervalId = null;
    const initialTimeout = setTimeout(() => {
      fetchGameData();
      intervalId = setInterval(fetchGameData, 1500);
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [pathname]);

  useEffect(() => {
    if (gameData) {
      const subData = gameData?.data?.data?.sub || [];

      // Map the data correctly:
      // sub[0-7]: Player 1-8 (Odds column)
      // sub[8-15]: Pair Plus 1-8 (Pair Plus column)
      // sub[16-23]: Total 1-8 (Total column)
      const mappedData = [];
      for (let i = 0; i < 8; i++) {
        const playerData = subData[i] || {}; // Player data (Odds)
        const pairPlusData = subData[i + 8] || {}; // Pair Plus data
        const totalData = subData[i + 16] || {}; // Total data

        mappedData.push({
          nat: playerData.nat || `Player ${i + 1}`,
          odds: playerData.b || 0,
          pairPlus: pairPlusData.b || 0,
          total: totalData.b || 0,
          gstatus: playerData.gstatus || "SUSPENDED",
          // Store full data for each column
          oddsData: playerData,
          pairPlusData: pairPlusData,
          totalData: totalData,
        });
      }
      setFilteredData(mappedData);
    }
  }, [gameData]);

  const fetchExposure = useCallback(async () => {
    try {
      const matchId = gameData?.data?.data?.mid?.toString();

      if (matchId) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user?.user_id || user?.id || '';
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              // Create keys for the team name as-is
              const keys = createExposureKeys(teamName);
              keys.forEach(key => {
                if (key) exposureMap[key] = exposureAmount;
              });
            }
          });
          setExposures(prev => ({ ...prev, ...exposureMap }));
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [gameData?.data?.data?.mid]);

  useEffect(() => {
    const matchId = gameData?.data?.data?.mid?.toString();
    if (!matchId) return;

    const timeoutId = setTimeout(() => {
      fetchExposure();
    }, 5000);

    const intervalId = setInterval(() => {
      fetchExposure();
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [gameData?.data?.data?.mid, fetchExposure]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleBetClick = (value, selection, betData, type) => {
    // Betting functionality not allowed in admin view
    console.log("Bet clicked:", { value, selection, betData, type });
  };

  // Get min/max values from first player data
  const minValue = filteredData[0]?.oddsData?.min || 100;
  const maxValue = filteredData[0]?.oddsData?.max || 100000;
  const pairPlusMin = filteredData[0]?.pairPlusData?.min || 100;
  const pairPlusMax = filteredData[0]?.pairPlusData?.max || 10000;

  // Format max value
  const formatMax = (value) => {
    if (!value) return "0";
    const num = parseFloat(value);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(0)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.sectionA}>
          <CasinoHeading
            name="TEENPATTI OPEN"
            roundId={gameData?.data?.data?.mid || "157250124095658"}
            placedBetCount={myBets.length}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {activeTab === "game" && (
            <>
              <VideoBox
                gameType="teen8"
                resultData={gameData?.data?.data?.card}
                iframeSrc={iframeSrc}
                timerValue={gameData?.data?.data?.lt}
              />

              <div className={styles.tableContainer}>
                <div className={styles.bettable}>
                  <BetTableTeen8
                    data={filteredData}
                    exposures={exposures}
                    min={minValue}
                    max={maxValue}
                    pairPlusMin={pairPlusMin}
                    pairPlusMax={pairPlusMax}
                    formatMax={formatMax}
                    cardString={gameData?.data?.data?.card || ""}
                  />
                </div>
              </div>
              <div className={styles.heading}>
                <div>Last Result</div>
                <Link to="/admin/reports/casinoresult/teen8">
                  <div className={styles.viewAll}>View All</div>
                </Link>
              </div>
              <ResultTeen8
                data={gameData?.lrs || []}
                gameData={gameData}
              />
            </>
          )}

        </div>

        <RightSection
          sectionKey="teen8"
          data={myBets}
          title="Matched"
          viewMoreText="View More"
          onViewMore={() => {
            // Handle view more action
            console.log("View more clicked for teen8");
          }}
        />
      </div>
    </Layout>
  );
}

